import React, { useEffect, useRef, useState } from 'react';
import { styled } from 'styled-components';
import PageHeader from '@src/components/PageHeader/PageHeader';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { generateCancelToken } from '@src/helpers/ApiHelper';
import { ApplicationContext } from '@src/contexts/ApplicationContext';
import { AxiosError, AxiosResponse } from 'axios';
import { useAppDispatch, useAppSelector } from '@src/store/hooks';
import { Alert, AlertColor, Snackbar } from '@mui/material';
import CustomAlert from '@src/components/CustomAlert/CustomAlert';
import { CatalogProductsListing } from '@src/components/ProductCatalog/CatalogProductsListing';
import ProductCreation from '@src/components/ProductCatalog/ProductCreation';
import {
	// setAllAttributes,
	// setAllCategories,
	// setAllProductsMasterCatalog,
	// setAllStockStatus,
	// setAllTaxClasses,
	setPageNumber,
	setProductCatalog,
} from '@src/store/reducers/catalogReducer';
import { debounce, fileErrors } from '@src/helpers/Helpers';
import ProductEditable from '@src/components/ProductCatalog/ProductEditable';
import { IsMobileWidth } from '@src/components/MediaQueryUtils';
import usePermission from '@src/hooks/usePermission';
import { ALL_PERMISSION_MAP } from '@src/helpers/Permission';
import { Filters, Option, TimeoutHandler } from '@src/interfaces/type';
import { Category } from '../MarketPlace';

const StyledContainer = styled.div`
	display: flex;
	flex-direction: column;
	height: 100%;
	background-color: var(--chat-header-bg);
	transition: width 0.3s ease;
`;

export interface FileError {
	name: string;
	message: string;
}
type Props = {};

export interface PartOptions {
	machineBrandOptions: Option[];
	groupOptions?: Option[];
	brandOptions: Option[];
	manufacturerOptions?: Option[];
	modelOptions: Option[];
	ownershipOptions?: Option[];
	dimensionOptions: Option[];
	diameterOptions?: Option[];
	designOptions: Option[];
	qualityOptions?: Option[];
	performanceOptions?: Option[];
	compositionOptions: Option[];
	weightOptions: Option[];
	subCategoryOptions?: Option[];
	categoryOptions?: Option[];
}

export type CustomAlertType = {
	message: string;
	severity: AlertColor;
	closeTime?: number;
};

export type ProductCatalog = {
	id: string;
	name: string;
	unit: string;
	price: string;
	category: string;
	image?: string;
	description?: string;
	customerPrice?: string;
	edit?: boolean;
	customerPriceValue?: string;
	editBtnDisable?: boolean;
	categoryId: string;
	images: string[];
	sku: string | null;
	model: string | null;
	quantity: number;
	attribute: {
		id: number;
		type: string;
		value: string;
	};
	subCategoryId: string;
	categoryObject: { id: string; value: string };
};

export const priceRegex = /^(\d*|\d+\.\d*|\.\d*)$/;

const Products: React.FC<Props> = ({}) => {
	const { apiService }: any = React.useContext(ApplicationContext);
	const { isDrawerOpen } = useAppSelector((state) => state?.UI);
	const isMobileWidth = IsMobileWidth();

	const pageContainerStyles: React.CSSProperties = {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		transition: 'width 0.3s ease',
		width:
			isDrawerOpen && !isMobileWidth
				? 'calc(100vw - 240px)'
				: !isDrawerOpen && !isMobileWidth
				? 'calc(100vw - 64px)'
				: '100%',
	};

	const timeoutHandler = useRef<TimeoutHandler>(null);

	const [isErrorVisible, setErrorVisible] = useState(false);
	const [alert, setAlert] = useState<CustomAlertType>({ message: '', severity: 'success' });
	const [errorMessage, setErrorMessage] = useState('');
	const [selectedRow, setSelectedRow] = useState<ProductCatalog | null>(null);
	const [loadingData, setLoadingData] = useState(true);
	const [loadingProducts, setLoadingProducts] = useState(false);
	const [products, setProducts] = useState<ProductCatalog[]>([]);
	const [categoryOptions, setCategoryOptions] = useState<Option[]>([]);
	const [attributeOptions, setAttributeOptions] = useState<Option[]>([]);
	const [taxClassesOptions, setTaxClassesOptions] = useState<Option[]>([]);
	const [stockStatusOptions, setStockStatusOptions] = useState<Option[]>([]);
	const [invalidFilesError, setInvalidFilesError] = useState<FileError[]>([]);
	const [operation, setOperation] = useState('');
	const [categoryId, setCategoryId] = useState(isMobileWidth ? 1000 : '');
	const [searchValue, setSearchValue] = useState('');
	const [searching, setSearching] = useState(false);
	const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
	const [productCount, setProductCount] = useState(0);
	const [selectedIndex, setSelectedIndex] = useState(1000);
	const [categories, setCategories] = useState<Category[]>([]);
	const [partOptions, setPartOptions] = useState<PartOptions | null>(null);
	const [filters, setFilters] = useState<Filters | null>(null);

	const [page, setPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);

	const [isApiCall, setApiCall] = useState(false);

	const location = useLocation();
	const navigate = useNavigate();
	const { productId } = useParams();

	const { id, catalogId, masterCatalogId } = useParams();
	const cancelTokenSourceRef: any = useRef();
	const dispatch = useAppDispatch();

	//Permission
	const checkPermission = usePermission();
	const canViewProduct = checkPermission(ALL_PERMISSION_MAP?.SUPPLIER?.PRODUCT?.VIEW, id);

	const handleRowClickListView = (params: any, event: any) => {
		const row = params?.row ? { ...params?.row } : { ...params };
		dispatch(setProductCatalog(row));
		setSelectedRow(row);
		navigate(`/suppliers/${id}/productCatalog/edit/${params?.id}`);
	};

	const displayError = (msg: string) => {
		setErrorMessage(msg);
		setErrorVisible(true);
	};

	const handleErrorClose = () => {
		setErrorMessage('');
		setErrorVisible(false);
	};

	const handleCloseFileErrors = (index: number) => {
		const updatedData = invalidFilesError?.filter((file, fileIndex) => index !== fileIndex);
		setInvalidFilesError(updatedData);
	};

	const activeBackIcon = () => {
		return (
			location?.pathname === `/suppliers/${id}/productCatalog/add` ||
			location?.pathname === `/suppliers/${id}/productCatalog/edit/${productId}`
		);
	};

	const handleArrowBack = () => {
		if (
			location?.pathname === `/suppliers/${id}/productCatalog/add` ||
			location?.pathname === `/suppliers/${id}/productCatalog/edit/${productId}`
		) {
			navigate(-1);
		}
	};

	const navigateToLastPage = (totalPages: number) => {
		setPage(totalPages);
		dispatch(setPageNumber(totalPages));

		navigate(`/suppliers/${id}/productCatalog?page=${totalPages}&limit=25`);
	};

	const handlePageChange = (event: any, value: number) => {
		if (page !== value) {
			setPage(value);
			// dispatch(setPageNumber(value));
			setLoadingData(true);
			setSelectedItemIds([]);
			const searchParams = new URLSearchParams();
			searchParams.set('page', value?.toString());
			searchParams.set('limit', '25');

			//update URL with new params values
			const newUrl = new URL(window.location.href);
			newUrl.search = searchParams.toString();
			window.history.replaceState({}, '', newUrl.toString());
		}
	};

	const setCategoriesforSlider = (supplierProductsResponse: any) => {
		apiService.getProductCategories(
			(res: AxiosResponse) => {
				const data = [{ category_id: 1000, name: 'All', image: '/Icons/All.png' }];

				setCategories([...data, ...res?.data?.data?.categories]);
			},
			(error: AxiosError) => {
				displayError('Something went wrong');
			}
		);
	};

	const getAllProducts = () => {
		const limit = 25;
		apiService.getProducts(
			(response: AxiosResponse) => {
				const data = response?.data?.data?.products?.map((product: any) => {
					return {
						...product,
						id: product?.id,
						name: product?.name,
						// unit: product?.attribute?.value,
						price: product?.price,
						category: product?.category?.name,
						categoryId: product?.category?.id,
						categoryObject: { id: product?.category?.id, value: product?.category?.name },
						description: product?.description,
						images: product?.images?.map((image: any) => {
							return { id: image?.order, url: image?.image, hover: false };
						}),
						sku: product?.sku,
						model: product?.model,
						quantity: product?.quantity,
						// attribute: product?.attribute,
						subCategory: product?.subCategory?.name,
						subCategoryId: product?.subCategory?.id,

						// taxId: product?.taxClass?.id,
						// stockStatusId: product?.stockStatus?.id,
						machineBrand: product?.machineBrand?.name,
						machineBrandId: product?.machineBrand?.id,
						machineModel: product?.machineModel?.name,
						machineModelId: product?.machineModel?.id,
						groupCategory: product?.groupCategory?.name,
						groupCategoryId: product?.groupCategory?.id,
						brand: product?.brand?.name,
						brandId: product?.brand?.id,
						partComposition: product?.partComposition?.name,
						partCompositionId: product?.partComposition?.id,
						partDesign: product?.partDesign?.name,
						partDesignId: product?.partDesign?.id,
						partDiameter: product?.partDiameter?.name,
						partDiameterId: product?.partDiameter?.id,
						partDimension: product?.partDimension?.name,
						partDimensionId: product?.partDimension?.id,
						partOwnership: product?.partOwnership?.name,
						partOwnershipId: product?.partOwnership?.id,
						partQuality: product?.partQuality?.name,
						partQualityId: product?.partQuality?.id,
						partWeight: product?.partWeight?.name,
						partWeightId: product?.partWeight?.id,
						partPerformance: product?.partPerformance?.name,
						partPerformanceId: product?.partPerformance?.id,
					};
				});

				const totalCount = response?.data?.data?.totalCount;
				const totalPages = Math.max(Math.ceil(totalCount / limit), 1);

				if (page === 1) {
					setProducts(data);
				} else {
					setProducts((prevState: any) => [...prevState, ...data]);
				}
				setProductCount(totalCount);
				setCategoriesforSlider(response?.data?.data?.products);
				setLoadingData(false);
				setSearching(false);
				setLoadingProducts(false);
				setTotalPages(totalPages);
				if (isApiCall) {
					setApiCall(false);
				}
				if (operation === 'add' || operation === 'delete') {
					setOperation('');
				}
				return;

				// setProductCount(totalCount);
				// setTotalPages(totalPages);
				// setProducts(data);
				// setLoadingProducts(false);
				// //Setting value in redux to get persisted data next time
				// // dispatch(setAllProductsMasterCatalog(data));

				// if (isApiCall) {
				// 	setApiCall(false);
				// }
				// if (operation === 'add' || operation === 'delete') {
				// 	navigateToLastPage(totalPages);
				// 	setOperation('');
				// }
				// setLoadingData(false);
				// setSearching(false);
			},
			(error: AxiosError) => {
				console.error(error);
				setLoadingData(false);
				setSearching(false);
				displayError('Something went wrong');
				setLoadingProducts(false);
			},
			{
				page: page,
				limit: limit,
				category: Number(categoryId) === 1000 ? '' : categoryId,
				search: searchValue,
				machineModel: filters?.model?.id,
				subCategory: filters?.subCategory?.id,
				machineBrand: filters?.machineBrand?.id,
				dimension: filters?.dimension?.id,
				design: filters?.design?.id,
				weight: filters?.weight?.id,
				composition: filters?.composition?.id,
				brand: filters?.brand?.id,
			}
		);
	};

	// useEffect(() => {
	// 	//persisted data
	// 	if (allProductsMasterCatalog?.length && !isMobileWidth) {
	// 		setLoadingData(false);
	// 		setCategoryOptions(allCategories);
	// 		setAttributeOptions(allAttributes);
	// 		setTaxClassesOptions(allTaxClasses);
	// 		setStockStatusOptions(allStockStatus);
	// 	}
	// }, []);

	// useEffect(() => {
	// 	//persisted data
	// 	if (allProductsMasterCatalog?.length && !isMobileWidth) {
	// 		setLoadingData(false);
	// 		setProducts(allProductsMasterCatalog);
	// 	}
	// }, []);

	useEffect(() => {
		if (canViewProduct) {
			getAllProducts();
		} else {
			setLoadingData(false);
		}
	}, [isApiCall, page, operation, categoryId, canViewProduct, filters]);

	useEffect(() => {
		if (canViewProduct) {
			//debounce
			if (timeoutHandler.current) {
				clearTimeout(timeoutHandler.current);
			}
			timeoutHandler.current = setTimeout(() => {
				try {
					getAllProducts();
				} catch (err) {}
			}, 500);
		} else {
			setLoadingData(false);
		}
	}, [searchValue, canViewProduct]);

	useEffect(() => {
		const getGroupsPromise: Promise<any> = new Promise((resolve, reject) => {
			apiService.getGroups(
				(response: AxiosResponse) => {
					const options = response?.data?.data?.map((group: any) => {
						return { value: group?.name, id: group?.group_category_id };
					});
					resolve(options);
				},
				(error: AxiosError) => reject(error)
			);
		});

		const getBrandsPromise: Promise<any> = new Promise((resolve, reject) => {
			apiService.getBrands(
				(response: AxiosResponse) => {
					const options = response?.data?.data?.map((brand: any) => {
						return { value: brand?.name, id: brand?.brand_id };
					});
					resolve(options);
				},
				(error: AxiosError) => reject(error)
			);
		});

		const getManufacturersPromise: Promise<any> = new Promise((resolve, reject) => {
			apiService.getManufacturers(
				(response: AxiosResponse) => {
					const options = response?.data?.data?.map((manufacturer: any) => {
						return { value: manufacturer?.name, id: manufacturer?.machine_manufacturer_id };
					});
					resolve(options);
				},
				(error: AxiosError) => reject(error)
			);
		});

		const getModelsPromise: Promise<any> = new Promise((resolve, reject) => {
			apiService.getModels(
				(response: AxiosResponse) => {
					const options = response?.data?.data?.map((model: any) => {
						return { value: model?.name, id: model?.model_id };
					});
					resolve(options);
				},
				(error: AxiosError) => reject(error)
			);
		});

		const getOwnershipsPromise: Promise<any> = new Promise((resolve, reject) => {
			apiService.getOwnerships(
				(response: AxiosResponse) => {
					const options = response?.data?.data?.map((ownership: any) => {
						return { value: ownership?.name, id: ownership?.part_ownership_id };
					});
					resolve(options);
				},
				(error: AxiosError) => reject(error)
			);
		});

		const getDimensionsPromise: Promise<any> = new Promise((resolve, reject) => {
			apiService.getDimensions(
				(response: AxiosResponse) => {
					const options = response?.data?.data?.map((dimension: any) => {
						return { value: dimension?.name, id: dimension?.part_dimension_id };
					});
					resolve(options);
				},
				(error: AxiosError) => reject(error)
			);
		});

		const getDiametersPromise: Promise<any> = new Promise((resolve, reject) => {
			apiService.getDiameters(
				(response: AxiosResponse) => {
					const options = response?.data?.data?.map((diameter: any) => {
						return { value: diameter?.name, id: diameter?.part_diameter_id };
					});
					resolve(options);
				},
				(error: AxiosError) => reject(error)
			);
		});

		const getDesignsPromise: Promise<any> = new Promise((resolve, reject) => {
			apiService.getDesigns(
				(response: AxiosResponse) => {
					const options = response?.data?.data?.map((design: any) => {
						return { value: design?.name, id: design?.part_design_id };
					});
					resolve(options);
				},
				(error: AxiosError) => reject(error)
			);
		});

		const getQualitiesPromise: Promise<any> = new Promise((resolve, reject) => {
			apiService.getQualities(
				(response: AxiosResponse) => {
					const options = response?.data?.data?.map((quality: any) => {
						return { value: quality?.name, id: quality?.part_quality_id };
					});
					resolve(options);
				},
				(error: AxiosError) => reject(error)
			);
		});

		const getPerformancesPromise: Promise<any> = new Promise((resolve, reject) => {
			apiService.getPerformances(
				(response: AxiosResponse) => {
					const options = response?.data?.data?.map((performance: any) => {
						return { value: performance?.name, id: performance?.part_performance_id };
					});
					resolve(options);
				},
				(error: AxiosError) => reject(error)
			);
		});

		const getCompositionsPromise: Promise<any> = new Promise((resolve, reject) => {
			apiService.getCompositions(
				(response: AxiosResponse) => {
					const options = response?.data?.data?.map((composition: any) => {
						return { value: composition?.name, id: composition?.part_composition_id };
					});
					resolve(options);
				},
				(error: AxiosError) => reject(error)
			);
		});

		const getWeightsPromise: Promise<any> = new Promise((resolve, reject) => {
			apiService.getWeights(
				(response: AxiosResponse) => {
					const options = response?.data?.data?.map((weight: any) => {
						return { value: weight?.name, id: weight?.part_weight_id };
					});
					resolve(options);
				},
				(error: AxiosError) => reject(error)
			);
		});

		Promise.all([
			getGroupsPromise,
			getBrandsPromise,
			getManufacturersPromise,
			getModelsPromise,
			getOwnershipsPromise,
			getDimensionsPromise,
			getDiametersPromise,
			getDesignsPromise,
			getQualitiesPromise,
			getPerformancesPromise,
			getCompositionsPromise,
			getWeightsPromise,
		])
			.then(
				([
					groupOptions,
					brandOptions,
					manufacturerOptions,
					modelOptions,
					ownershipOptions,
					dimensionOptions,
					diameterOptions,
					designOptions,
					qualityOptions,
					performanceOptions,
					compositionOptions,
					weightOptions,
				]) => {
					const data = {
						groupOptions,
						brandOptions,
						manufacturerOptions,
						modelOptions,
						ownershipOptions,
						dimensionOptions,
						diameterOptions,
						designOptions,
						qualityOptions,
						performanceOptions,
						compositionOptions,
						weightOptions,
					};

					//@ts-ignore
					setPartOptions(data);
				}
			)
			.catch((error: AxiosError) => {
				console.error(error);
				displayError('Something went wrong');
			});
	}, []);

	const productsRoutes = (path: string, id: string | undefined, catalogId: string | undefined) => {
		switch (path) {
			case `/suppliers/${id}/productCatalog/add`:
				return (
					<ProductCreation
						displayError={displayError}
						categoryOptions={categoryOptions}
						attributeOptions={attributeOptions}
						taxClassesOptions={taxClassesOptions}
						stockStatusOptions={stockStatusOptions}
						setAlert={setAlert}
						setInvalidFilesError={setInvalidFilesError}
						totalPages={totalPages}
						setOperation={setOperation}
						setPage={setPage}
						setApiCall={setApiCall}
						partOptions={partOptions}
					/>
				);
			case `/suppliers/${id}/productCatalog/edit/${productId}`:
				return (
					<ProductEditable
						selectedRow={selectedRow}
						setSelectedRow={setSelectedRow}
						setAlert={setAlert}
						displayError={displayError}
						categoryOptions={categoryOptions}
						attributeOptions={attributeOptions}
						taxClassesOptions={taxClassesOptions}
						stockStatusOptions={stockStatusOptions}
						setInvalidFilesError={setInvalidFilesError}
						setApiCall={setApiCall}
						partOptions={partOptions}
					/>
				);
			default:
				return (
					<CatalogProductsListing
						products={products}
						setProducts={setProducts}
						categoryOptions={partOptions?.groupOptions}
						brandOptions={partOptions?.brandOptions}
						machineBrandOptions={partOptions?.manufacturerOptions}
						modelOptions={partOptions?.modelOptions}
						attributeOptions={attributeOptions}
						taxClassesOptions={taxClassesOptions}
						stockStatusOptions={stockStatusOptions}
						setAlert={setAlert}
						displayError={displayError}
						loadingData={loadingData}
						handleRowClick={handleRowClickListView}
						setOperation={setOperation}
						setApiCall={setApiCall}
						totalPages={totalPages}
						page={page}
						setPage={setPage}
						categoryId={categoryId}
						setCategoryId={setCategoryId}
						handleChange={handlePageChange}
						searchValue={searchValue}
						setSearchValue={setSearchValue}
						searching={searching}
						setSearching={setSearching}
						selectedItemIds={selectedItemIds}
						setSelectedItemIds={setSelectedItemIds}
						productCount={productCount}
						categories={categories}
						setCategories={setCategories}
						loadingProducts={loadingProducts}
						setLoadingProducts={setLoadingProducts}
						partOptions={partOptions}
						filters={filters}
						setFilters={setFilters}
					/>
				);
		}
	};

	return (
		<StyledContainer style={pageContainerStyles}>
			<PageHeader
				title="Product Catalog"
				activeIcon={activeBackIcon()}
				handleArrowBack={handleArrowBack}
			/>

			{productsRoutes(location.pathname, id, catalogId)}

			{alert?.message !== '' ? <CustomAlert alert={alert} setAlert={setAlert} /> : null}
			<Snackbar
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				open={isErrorVisible}
				autoHideDuration={6000}
				onClose={handleErrorClose}
			>
				<Alert onClose={handleErrorClose} severity="error" elevation={4}>
					{errorMessage}
				</Alert>
			</Snackbar>
			{invalidFilesError?.map((file, index) => {
				return (
					<Snackbar
						key={index}
						anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
						open={file?.name !== ''}
						autoHideDuration={6000}
						onClose={() => handleCloseFileErrors(index)}
						style={{ marginBottom: `${index * 70}px` }}
					>
						<Alert onClose={() => handleCloseFileErrors(index)} severity="error" elevation={4}>
							{fileErrors(file?.name, file?.message)}
						</Alert>
					</Snackbar>
				);
			})}
		</StyledContainer>
	);
};

export default Products;
